import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { RouterLink } from '@angular/router';
import { NbButtonModule, NbIconModule } from '@nebular/theme';
import { Observable } from 'rxjs';
import { DropshipCart, DropshipMoney } from 'shared';
import {
  CartResetError,
  ExclusivesCartService
} from '../../../../exclusives/exclusives.cart.service';
import { WithWidthPipe } from '../../../../exclusives/image-url';
import { DeleteButtonComponent } from '../../../delete-button/delete-button.component';
import { AnalyticsService } from '../../../../services/analytics.service';

/**
 * Subtotal at which shipping becomes free, in the cart's own currency. The threshold
 * itself qualifies, so $99.00 exactly ships free. Mirrors the shipping rule configured
 * in Shopify — if that rule changes, this must change with it, or the drawer promises
 * free shipping that checkout won't honour.
 */
const FREE_SHIPPING_THRESHOLD = 99;

/** Free-shipping state for the current subtotal, rendered as a strip above the total. */
export interface FreeShippingProgress {
  qualified: boolean;
  /** Formatted shortfall (e.g. "$20.00"). Empty once qualified. */
  remaining: string;
}

@Component({
  selector: 'app-exclusives-tab',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    NbButtonModule,
    NbIconModule,
    WithWidthPipe,
    DeleteButtonComponent
  ],
  templateUrl: './exclusives-tab.component.html',
  styleUrl: './exclusives-tab.component.scss'
})
export class ExclusivesTabComponent {
  readonly cart$: Observable<DropshipCart | null>;
  pendingLineIds = new Set<string>();
  pendingError: string | null = null;
  lineWarnings = new Map<string, string>();
  cartResetBanner: string | null = null;

  constructor(
    private readonly cart: ExclusivesCartService,
    private readonly analytics: AnalyticsService
  ) {
    this.cart$ = this.cart.activeCart$();
  }

  formatPrice(money: DropshipMoney): string {
    const amount = parseFloat(money.amount);
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: money.currencyCode
      }).format(amount);
    } catch {
      return `${amount.toFixed(2)} ${money.currencyCode}`;
    }
  }

  /**
   * How far the cart is from free shipping. The threshold qualifies, so any remaining
   * shortfall is strictly positive and the prompt never reads "Add $0.00".
   */
  freeShipping(cart: DropshipCart): FreeShippingProgress {
    const subtotal = parseFloat(cart.subtotal.amount);
    if (!Number.isFinite(subtotal)) {
      return { qualified: false, remaining: '' };
    }
    const shortfall = FREE_SHIPPING_THRESHOLD - subtotal;
    if (shortfall <= 0) {
      return { qualified: true, remaining: '' };
    }
    return {
      qualified: false,
      remaining: this.formatPrice({
        amount: shortfall.toFixed(2),
        currencyCode: cart.subtotal.currencyCode
      })
    };
  }

  isPending(lineId: string): boolean {
    return this.pendingLineIds.has(lineId);
  }

  warningFor(lineId: string): string | null {
    return this.lineWarnings.get(lineId) ?? null;
  }

  async increment(lineId: string, currentQty: number): Promise<void> {
    const requestedQty = currentQty + 1;
    const cart = await this.runLineOp(lineId, () =>
      this.cart.updateLine(lineId, requestedQty)
    );
    if (!cart) return;
    const line = cart.lines.find((l) => l.id === lineId);
    if (line && line.quantity < requestedQty) {
      this.lineWarnings.set(
        lineId,
        line.quantity > 0
          ? `Only ${line.quantity} available in stock.`
          : 'This item is out of stock.'
      );
    }
  }

  async decrement(lineId: string, currentQty: number): Promise<void> {
    if (currentQty <= 1) return;
    await this.runLineOp(lineId, () => this.cart.updateLine(lineId, currentQty - 1));
  }

  async remove(lineId: string): Promise<void> {
    await this.runLineOp(lineId, () => this.cart.removeLine(lineId));
  }

  checkout(cart: DropshipCart): void {
    if (!cart.checkoutUrl) return;
    this.analytics.logEvent(
      'begin_checkout',
      {
        currency: cart.subtotal.currencyCode,
        value: parseFloat(cart.subtotal.amount),
        line_count: cart.lines.length,
        item_count: cart.lines.reduce((n, l) => n + l.quantity, 0),
        source: 'exclusives'
      },
      false
    );
    window.location.href = cart.checkoutUrl;
  }

  dismissCartResetBanner(): void {
    this.cartResetBanner = null;
  }

  private async runLineOp(
    lineId: string,
    op: () => Promise<DropshipCart>
  ): Promise<DropshipCart | null> {
    if (this.pendingLineIds.has(lineId)) return null;
    this.pendingLineIds.add(lineId);
    this.pendingError = null;
    this.lineWarnings.delete(lineId);
    try {
      return await op();
    } catch (err) {
      if (err instanceof CartResetError) {
        this.cartResetBanner = 'Your cart was reset. Please re-add items.';
      } else {
        this.pendingError = 'Could not update cart. Please try again.';
      }
      return null;
    } finally {
      this.pendingLineIds.delete(lineId);
    }
  }
}
